import { useState, useEffect } from 'react'
import { apiService } from '../../services/api'
import { fetchAlbatrossCompetitions, type AlbatrossCompetition } from '../../services/albatross'
import { Competition } from '../../types'

const emptyForm = {
  year: '',
  name: '',
  startDate: '',
  endDate: '',
  location: '',
  description: '',
  albatrossCompId: '',
}

const emptySettings = {
  maxParticipants: '',
  photoThemesPerDay: '',
  busGroupsEnabled: false,
  registrationOpen: false,
}

export default function CompetitionManagement() {
  const [competitions, setCompetitions] = useState<Competition[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingCompetition, setEditingCompetition] = useState<Competition | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [settingsData, setSettingsData] = useState(emptySettings)
  const [submitting, setSubmitting] = useState(false)
  const [albatrossComps, setAlbatrossComps] = useState<AlbatrossCompetition[]>([])
  const [albatrossLoading, setAlbatrossLoading] = useState(false)
  const [albatrossFetchFailed, setAlbatrossFetchFailed] = useState(false)

  useEffect(() => { loadCompetitions() }, [])
  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 5000); return () => clearTimeout(t) }
  }, [toast])

  const loadCompetitions = async () => {
    setLoading(true)
    setError(null)
    const response = await apiService.getCompetitions()
    if (response.error) {
      setError(response.error.message)
      setLoading(false)
      return
    }
    if (response.data) {
      const data = response.data as any
      const arr = data?.data?.competitions || data?.competitions || data?.data || []
      setCompetitions(Array.isArray(arr) ? arr : [])
    } else {
      setCompetitions([])
    }
    setLoading(false)
  }

  const loadAlbatrossComps = async () => {
    setAlbatrossLoading(true)
    setAlbatrossFetchFailed(false)
    try {
      const comps = await fetchAlbatrossCompetitions()
      setAlbatrossComps(comps)
    } catch {
      setAlbatrossFetchFailed(true)
    }
    setAlbatrossLoading(false)
  }

  const resetForm = () => {
    setFormData(emptyForm)
    setSettingsData(emptySettings)
    setEditingCompetition(null)
    setShowCreateForm(false)
  }

  const handleEdit = (competition: Competition) => {
    setEditingCompetition(competition)
    loadAlbatrossComps()
    setFormData({
      year: competition.year,
      name: competition.name,
      startDate: competition.startDate?.slice(0, 10) || '',
      endDate: competition.endDate?.slice(0, 10) || '',
      location: competition.location || '',
      description: competition.description || '',
      albatrossCompId: competition.albatrossCompId || '',
    })
    setSettingsData({
      maxParticipants: competition.settings?.maxParticipants?.toString() || '',
      photoThemesPerDay: competition.settings?.photoThemesPerDay?.toString() || '',
      busGroupsEnabled: competition.settings?.busGroupsEnabled ?? false,
      registrationOpen: competition.settings?.registrationOpen ?? false,
    })
    setShowCreateForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    const settings: Competition['settings'] = {}
    if (settingsData.maxParticipants) settings.maxParticipants = parseInt(settingsData.maxParticipants)
    if (settingsData.photoThemesPerDay) settings.photoThemesPerDay = parseInt(settingsData.photoThemesPerDay)
    settings.busGroupsEnabled = settingsData.busGroupsEnabled
    settings.registrationOpen = settingsData.registrationOpen

    if (editingCompetition) {
      const updateData = {
        year: formData.year,
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
        location: formData.location,
        description: formData.description || null,
        albatrossCompId: formData.albatrossCompId || null,
        settings,
      }
      const response = await apiService.updateCompetition(editingCompetition.competitionId, updateData)
      setSubmitting(false)
      if (response.error) {
        setToast({ message: response.error.message, type: 'error' })
        return
      }
      // Optimistically update local state from the response to avoid eventual consistency lag
      if (response.data) {
        const respData = response.data as any
        const updated = respData?.data?.competition
        if (updated) {
          setCompetitions(prev => prev.map(c => c.competitionId === updated.competitionId ? { ...c, ...updated } : c))
        }
      }
      setToast({ message: 'Competition updated', type: 'success' })
    } else {
      const response = await apiService.createCompetition({
        year: formData.year,
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
        location: formData.location,
        description: formData.description || undefined,
      })
      setSubmitting(false)
      if (response.error) {
        setToast({ message: response.error.message, type: 'error' })
        return
      }
      setToast({ message: 'Competition created', type: 'success' })
    }
    resetForm()
    loadCompetitions()
  }

  const handleSetActive = async (competitionId: string) => {
    const response = await apiService.setActiveCompetition(competitionId)
    if (response.error) {
      setToast({ message: response.error.message, type: 'error' })
      return
    }
    setToast({ message: 'Competition set as active', type: 'success' })
    loadCompetitions()
  }

  if (loading) return (
    <div className="flex justify-center items-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  )

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <p className="text-red-800">{error}</p>
      <button onClick={loadCompetitions} className="mt-2 text-red-600 hover:text-red-800 font-medium">Try Again</button>
    </div>
  )

  return (
    <div className="space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          <div className="flex items-center gap-2">
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-white/80 hover:text-white">&times;</button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Competition Management</h2>
        <button onClick={() => { if (showCreateForm) { resetForm() } else { setShowCreateForm(true) } }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          {showCreateForm ? 'Cancel' : 'Create Competition'}
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            {editingCompetition ? 'Edit Competition' : 'Create New Competition'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="comp-year" className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                <input id="comp-year" type="text" required value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  placeholder="e.g. 2025"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label htmlFor="comp-name" className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input id="comp-name" type="text" required value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Competition name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="comp-start" className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                <input id="comp-start" type="date" required value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label htmlFor="comp-end" className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                <input id="comp-end" type="date" required value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>
            <div>
              <label htmlFor="comp-location" className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
              <input id="comp-location" type="text" required value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Competition location"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div>
              <label htmlFor="comp-desc" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea id="comp-desc" value={formData.description} rows={3}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>

            {editingCompetition && (
              <>
                <hr className="border-gray-200" />
                <h4 className="text-md font-semibold text-gray-800">Competition Settings</h4>
                <div>
                  <label htmlFor="comp-albatross-id" className="block text-sm font-medium text-gray-700 mb-1">Albatross Competition ID</label>
                  {albatrossLoading ? (
                    <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      Loading Albatross competitions...
                    </div>
                  ) : albatrossFetchFailed ? (
                    <input id="comp-albatross-id" type="text" value={formData.albatrossCompId}
                      onChange={(e) => setFormData({ ...formData, albatrossCompId: e.target.value })}
                      placeholder="e.g. comp-123 (could not load competitions)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  ) : (
                    <select id="comp-albatross-id" value={formData.albatrossCompId}
                      onChange={(e) => setFormData({ ...formData, albatrossCompId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="">-- Select Albatross Competition --</option>
                      {albatrossComps.map((ac) => (
                        <option key={ac.id} value={ac.id}>
                          {ac.name} ({ac.location}, {ac.startDate})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="comp-max" className="block text-sm font-medium text-gray-700 mb-1">Max Participants</label>
                    <input id="comp-max" type="number" min="1" value={settingsData.maxParticipants}
                      onChange={(e) => setSettingsData({ ...settingsData, maxParticipants: e.target.value })}
                      placeholder="No limit"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label htmlFor="comp-themes" className="block text-sm font-medium text-gray-700 mb-1">Photo Themes Per Day</label>
                    <input id="comp-themes" type="number" min="1" value={settingsData.photoThemesPerDay}
                      onChange={(e) => setSettingsData({ ...settingsData, photoThemesPerDay: e.target.value })}
                      placeholder="e.g. 1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <label className="flex items-center">
                    <input type="checkbox" checked={settingsData.busGroupsEnabled}
                      onChange={(e) => setSettingsData({ ...settingsData, busGroupsEnabled: e.target.checked })}
                      className="mr-2 h-4 w-4 text-blue-600 rounded" />
                    <span className="text-sm font-medium text-gray-700">Bus Groups Enabled</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" checked={settingsData.registrationOpen}
                      onChange={(e) => setSettingsData({ ...settingsData, registrationOpen: e.target.checked })}
                      className="mr-2 h-4 w-4 text-blue-600 rounded" />
                    <span className="text-sm font-medium text-gray-700">Registration Open</span>
                  </label>
                </div>
              </>
            )}

            <div className="flex gap-3">
              <button type="button" onClick={resetForm}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                {submitting ? 'Saving...' : editingCompetition ? 'Update Competition' : 'Create Competition'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Competitions List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Competition</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {competitions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    No competitions found. Create a competition to get started.
                  </td>
                </tr>
              ) : competitions.map((comp) => (
                <tr key={comp.competitionId} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{comp.name}</div>
                        <div className="text-sm text-gray-500">Year: {comp.year}</div>
                        {comp.albatrossCompId && (
                          <div className="text-xs text-indigo-600 mt-0.5">Albatross: {comp.albatrossCompId}</div>
                        )}
                      </div>
                      {comp.isActive && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {comp.startDate ? new Date(comp.startDate).toLocaleDateString() : '—'}
                    </div>
                    <div className="text-sm text-gray-500">
                      to {comp.endDate ? new Date(comp.endDate).toLocaleDateString() : '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {comp.location || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {comp.participantCount ?? 0} participants
                    </div>
                    {comp.settings?.registrationOpen && (
                      <span className="text-xs text-blue-600">Registration open</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => handleEdit(comp)}
                        className="text-blue-600 hover:text-blue-900">Edit</button>
                      {!comp.isActive && (
                        <button onClick={() => handleSetActive(comp.competitionId)}
                          className="text-green-600 hover:text-green-900">Set Active</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
